import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import api from "./api";
import axios from 'axios';
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { WebSocketContext } from "./WebSockectContext";

export const AuthContext = createContext();

// Must match the key Deposit.js used to persist the reference so a pending
// deposit started before this context loaded is still picked up.
const PENDING_DEPOSIT_STORAGE_KEY = "huntexlotto.pendingDepositReference";
const PENDING_DEPOSIT_POLL_MS = 5000;

export const AuthProvider = ({ children, navigation }) => {
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [userBalance, setUserBalance] = useState(0);
  // Withdrawal account: winnings only, separate from the playing balance.
  const [winBalance, setWinBalance] = useState(0);
  const [role, setRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginInput, setLoginInput] = useState("");
  const [fullName, setFullName] = useState('');
  // Needed to tell our own balance pushes apart from other players'.
  const [userId, setUserId] = useState(null);
  const { ws } = useContext(WebSocketContext) || {};
  // Tracked here (not in Deposit.js) so a delayed mobile money deposit keeps
  // being verified even if the user leaves the Deposit page or reloads.
  const [pendingDepositReference, setPendingDepositReference] = useState(() => {
    try {
      return window.localStorage.getItem(PENDING_DEPOSIT_STORAGE_KEY) || null;
    } catch (error) {
      return null;
    }
  });

  const loadToken = async () => {
    try {
      const storedToken = localStorage.getItem("token");
      const storedRole = localStorage.getItem("role");
      if (storedToken && storedRole) {
        setToken(storedToken);
        setRole(storedRole);
        setIsLoggedIn(true);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadToken();
  }, []);

  const getLoginErrorMessage = (error) => {
    if (axios.isAxiosError(error)) {
      const isTimeout =
        error.code === "ECONNABORTED" ||
        /timeout/i.test(error.message || "");

      if (isTimeout) {
        return "Connection timed out. Please check your internet connection and try again.";
      }

      if (!error.response) {
        return "Unable to reach the server. Please check your internet connection and try again.";
      }

      return error.response?.data?.message || "Login failed. Please try again.";
    }

    return error?.message || "Login failed. Please try again.";
  };

  const normalizePhoneForLogin = (input) => {
    const cleanedInput = String(input || "").trim().replace(/[\s()-]/g, "");
    if (!cleanedInput) return null;

    const candidates = [cleanedInput];

    if (/^\d+$/.test(cleanedInput)) {
      candidates.push(`+${cleanedInput}`);
      if (cleanedInput.startsWith("233")) {
        candidates.push(`0${cleanedInput.slice(3)}`);
      }
    }

    for (const candidate of candidates) {
      const parsed = candidate.startsWith("+")
        ? parsePhoneNumberFromString(candidate)
        : parsePhoneNumberFromString(candidate, "GH");

      if (parsed && parsed.isValid()) {
        return parsed.format("E.164");
      }
    }

    return null;
  };

  const handleLogin = async () => {
    setLoading(true);
    const loginInput = String(phoneNumber || "").trim();
    const compactLoginInput = loginInput.replace(/\s+/g, "");
    const normalizedPhone = normalizePhoneForLogin(loginInput);

    // Validate input format
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginInput);
    const isPhoneNumber = Boolean(normalizedPhone);
    const isAdminKey = /^\d{8,}$/.test(compactLoginInput);

    let loginType = "";
    let loginIdentifier = "";

    try {
      if (isEmail) {
        loginType = "email";
        loginIdentifier = loginInput;
      } else if (isPhoneNumber) {
        loginType = "phoneNumber";
        loginIdentifier = normalizedPhone;
      } else if (isAdminKey) {
        // Admin login via admin key
        loginType = "adminKey";
        loginIdentifier = compactLoginInput;
      } else {
        // Generic message for any invalid login input
        throw new Error("Invalid credentials. Please check your input or SignUp");
      }

      // Make login API request
      const response = await api.post(
        "/login",
        {
          loginType,
          loginIdentifier,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 5000,
        }
      );

      if (!response.data.token) {
        throw new Error("Token not found in response");
      }

      const receivedToken = response.data.token;
      const userRole = response.data.user?.role || "user";
      const userData = response.data.user;

      // Update state with token and role
      setToken(receivedToken);
      setRole(userRole);
      setUserData(userData);
      setIsLoggedIn(true);

      // Persist to localStorage
      localStorage.setItem("token", receivedToken);
      localStorage.setItem("role", userRole);
      if (userData) {
        localStorage.setItem("userData", JSON.stringify(userData));
      }

      // Fetch additional user data (balance, profile, etc.)
      await fetchUserData(receivedToken);

      return true;
    } catch (error) {
      // Clear states on error
      const errorMessage = getLoginErrorMessage(error);
      alert(`Login Error: ${errorMessage}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      setToken(null);
      setRole(null);
      setUserData(null);
      setUserBalance(0);
      setWinBalance(0);
      setUserId(null);
      setIsLoggedIn(false);
      setPhoneNumber("");
      setFullName("");
      setPassword("");
      
      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("userData");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Stable identity: callers put this in effect dependency arrays to drive
  // polling, and a fresh function every render would tear the interval down
  // and rebuild it on each refresh, so it would never settle into a rhythm.
  const fetchUserData = useCallback(async (overrideToken) => {
    // Callers that already hold a fresh token pass it in; everyone else
    // (e.g. a refresh after a withdrawal) falls back to the stored token.
    const activeToken = overrideToken || token || localStorage.getItem("token");

    if (activeToken) {
      try {
        const response = await api.get('/user', {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        const { phoneNumber, fullName, balance, winBalance } = response.data;
        setPhoneNumber(phoneNumber);
        setFullName(fullName);
        setUserId(response.data.userId ? String(response.data.userId) : null);
        setUserBalance(balance || 0);
        setWinBalance(winBalance || 0);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        // Don't throw — allow user to proceed even if profile fetch fails
        return null
      }
    }
  }, [token]);

  useEffect(() => {
    fetchUserData(token);
  }, [token, fetchUserData]);

  const registerPendingDeposit = useCallback((reference) => {
    if (!reference) return;
    setPendingDepositReference(reference);
    try {
      window.localStorage.setItem(PENDING_DEPOSIT_STORAGE_KEY, reference);
    } catch (error) {
      // Ignore storage errors (private mode/storage restrictions).
    }
  }, []);

  const clearPendingDeposit = useCallback(() => {
    setPendingDepositReference(null);
    try {
      window.localStorage.removeItem(PENDING_DEPOSIT_STORAGE_KEY);
    } catch (error) {
      // Ignore storage errors (private mode/storage restrictions).
    }
  }, []);

  // Keeps polling /deposit/verify regardless of which page is mounted, so a
  // deposit that only confirms after the user leaves Deposit.js (or the
  // webhook is delayed/missed) still gets credited to the balance.
  useEffect(() => {
    if (!pendingDepositReference || !token) return undefined;

    let cancelled = false;

    const verify = async () => {
      try {
        const response = await api.get(`/deposit/verify/${pendingDepositReference}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const status = String(response.data?.data?.status || "").toLowerCase();

        if (["success", "successful", "succeeded"].includes(status)) {
          clearPendingDeposit();
          fetchUserData(token);
        } else if (["failed", "abandoned", "reversed", "cancelled", "canceled"].includes(status)) {
          clearPendingDeposit();
        }
      } catch (error) {
        // Transient network/server errors shouldn't drop the pending deposit; keep retrying.
      }
    };

    verify();
    const intervalId = window.setInterval(verify, PENDING_DEPOSIT_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [pendingDepositReference, token, fetchUserData, clearPendingDeposit]);

  // Both balances are kept live here rather than per page, so winnings update
  // wherever the user is. The server pushes three shapes: {event, data} from
  // the deposit and withdrawal routes, and {type: "balance_update"} from
  // saveNumbers, which moves both accounts at once.
  useEffect(() => {
    if (!ws || !userId) return;

    const applyBalance = (setBalance, value) => {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        setBalance(numericValue);
      }
    };

    const handleMessage = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (error) {
        return;
      }

      const payload = message.data || message;

      // Every socket receives every broadcast, so an unaddressed message —
      // or one addressed to someone else — must not touch this wallet.
      if (!payload.userId || String(payload.userId) !== userId) return;

      switch (message.event || message.type) {
        case "userBalanceUpdate":
          applyBalance(setUserBalance, payload.userBalance);
          break;
        case "winBalanceUpdate":
          applyBalance(setWinBalance, payload.winBalance);
          break;
        case "referral_commission":
          applyBalance(setUserBalance, payload.balance);
          break;
        case "balance_Update":
        case "balance_update":
          applyBalance(setUserBalance, payload.balance);
          applyBalance(setWinBalance, payload.winBalance);
          break;
        default:
          break;
      }
    };

    ws.addEventListener("message", handleMessage);
    return () => ws.removeEventListener("message", handleMessage);
  }, [ws, userId]);

  return (
    <AuthContext.Provider
      value={{
        loading,
        setLoading,
        setPassword,
        password,
        setLoginInput,
        loginInput,
        token,
        setToken,
        handleLogin,
        handleLogout,
        phoneNumber,
        setPhoneNumber,
        userData,
        setUserData,
        fetchUserData,
        userBalance,
        setUserBalance,
        winBalance,
        setWinBalance,
        role,
        loadToken,
        isLoggedIn, 
        setIsLoggedIn,
        fullName,
        setFullName, 
        pendingDepositReference,
        registerPendingDeposit,
        clearPendingDeposit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
